import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Estimation } from '../../models/estimacion.model';
import { EstimationProjectType } from '../../models/estimation-v3.types';

/**
 * Línea horizontal compacta que sustituye al banner v3 grande.
 * Muestra solo lo crítico que el usuario necesita ver siempre:
 *   - Tipo de proyecto (pill neutro, sin alarmismo).
 *   - Total con contingencia (lectura grande).
 *   - Contador de avisos clickable.
 *
 * El resto de info (confianza, basis, desglose P50/Contingencia, calibración,
 * diagnóstico) vive en el drawer extendido.
 */
@Component({
  selector: 'app-estimation-quick-bar',
  templateUrl: './estimation-quick-bar.component.html',
  styleUrls: ['./estimation-quick-bar.component.scss']
})
export class EstimationQuickBarComponent {
  @Input() estimation!: Estimation;

  @Output() openWarnings = new EventEmitter<void>();

  get hasMetadata(): boolean {
    return !!this.estimation?.metadata;
  }

  get projectType(): EstimationProjectType | undefined {
    return this.estimation?.metadata?.project_type_classified;
  }

  get projectTypeLabel(): string {
    const map: Record<EstimationProjectType, string> = {
      NEW: 'Proyecto nuevo',
      EVOLUTIONARY: 'Evolutivo',
      RECURRING: 'Recurrente',
      MAINTENANCE: 'Mantenimiento',
      UNKNOWN: 'Sin clasificar'
    };
    return this.projectType ? map[this.projectType] : '';
  }

  /**
   * Devuelve el término del glosario asociado al tipo de proyecto, o
   * cadena vacía si no hay un término específico para ese tipo.
   */
  get projectTypeGlossaryTerm(): string {
    const map: Record<string, string> = {
      NEW: 'project_type_new',
      EVOLUTIONARY: 'project_type_evolutionary',
      RECURRING: 'project_type_recurring',
      MAINTENANCE: 'project_type_maintenance'
    };
    return this.projectType ? (map[this.projectType] || '') : '';
  }

  /** Tooltip del total: explica el desglose si hay datos. */
  get totalTooltip(): string {
    const r = this.estimation?.rollup;
    if (!r) return '';
    const p50 = r.total_p50_hours;
    const cont = r.contingency_buffer_hours;
    const total = r.total_with_contingency_hours;
    if (typeof p50 === 'number' && typeof cont === 'number' && typeof total === 'number') {
      return `Total con contingencia: ${total}H = ${p50}H P50 + ${cont}H contingencia.`;
    }
    return 'Total con contingencia incluida.';
  }

  get totalWithContingency(): number | undefined {
    return this.estimation?.rollup?.total_with_contingency_hours;
  }

  get hasTotal(): boolean {
    return typeof this.totalWithContingency === 'number';
  }

  get warningCount(): number {
    return this.estimation?.metadata?.warnings?.length ?? 0;
  }

  onWarningsClick(): void {
    if (this.warningCount > 0) {
      this.openWarnings.emit();
    }
  }
}
